import { useMemo, useState } from "react";
import type { Track } from "../../shared/domain.ts";
import { CUSTOM_MIX_ID, MIXES, mixProgress } from "../game/mixes.ts";
import { filterKey, type Filter } from "../game/pools.ts";
import { CustomMix } from "./CustomMix.tsx";

/**
 * Unlimited's setup screen — pick a Mix, then play.
 *
 * This exists so the Filters stop living inside the game. Choosing is a moment,
 * not a permanent strip of controls above the Board, and the cards spend the
 * seen-sets the game was already storing on something the player can see: how
 * much of each Mix they have heard.
 */
export function MixPicker({
  tracks,
  seen,
  custom,
  onChoose,
  onCancel,
}: {
  tracks: Track[];
  seen: Record<string, number[]>;
  custom: Filter;
  onChoose: (mixId: string, customFilter?: Filter) => void;
  /** Only given while a Round is already in play — see `Unlimited` in App.tsx. */
  onCancel?: () => void;
}) {
  const [draft, setDraft] = useState<Filter>(custom);
  const [building, setBuilding] = useState(false);

  /**
   * Memoised because `mixProgress` walks the whole Catalog once per Mix.
   *
   * Uncached, thirteen Mixes meant thirteen full-Catalog passes and thirteen new
   * arrays on *every render* — including every keystroke-equivalent, since a chip
   * tap in CustomMix re-renders this component. At 20,000 Tracks that is ~260,000
   * predicate calls per tap, which is a visible stall for a number that did not
   * change.
   */
  const progress = useMemo(
    () =>
      MIXES.map((mix) => ({
        mix,
        ...mixProgress(tracks, mix.filter, seen[filterKey(mix.filter)]),
      })),
    [tracks, seen],
  );

  return (
    <section className="picker">
      <header className="picker__head">
        <h2 className="picker__title">Pick a mix</h2>
        {onCancel && (
          <button className="picker__cancel" type="button" onClick={onCancel}>
            Cancel
          </button>
        )}
      </header>

      <ul className="mixes">
        {progress.map(({ mix, pool, heard }) => (
          <li key={mix.id}>
            <button
              className="mix-card"
              type="button"
              onClick={() => onChoose(mix.id)}
              style={{ "--tile-hue": mix.hue } as React.CSSProperties}
            >
              <span className="mix-card__glyph" aria-hidden="true">
                {mix.glyph}
              </span>

              <span className="mix-card__body">
                <span className="mix-card__name">{mix.name}</span>
                <span className="mix-card__blurb">{mix.blurb}</span>
                <span className="mix-card__meta">
                  heard {heard} of {pool}
                </span>
                {/* A bar rather than only a fraction: "8 of 7993" reads as
                    nothing, and the point is how much is left. */}
                <span className="mix-card__bar" aria-hidden="true">
                  <i style={{ width: `${pool === 0 ? 0 : Math.max(2, (heard / pool) * 100)}%` }} />
                </span>
              </span>
            </button>
          </li>
        ))}

        <li>
          {/* Deliberately louder than the rest: the genre and era picker lives
              behind this card, and it was easy to scroll past thirteen cards
              without ever noticing it was here. */}
          <button
            className={`mix-card mix-card--custom${building ? " mix-card--on" : ""}`}
            type="button"
            aria-expanded={building}
            onClick={() => setBuilding((b) => !b)}
          >
            <span className="mix-card__glyph" aria-hidden="true">
              🎛️
            </span>
            <span className="mix-card__body">
              <span className="mix-card__name">Pick your own genres</span>
              <span className="mix-card__blurb">Any mix of genres and eras</span>
            </span>
            <span className="mix-card__chev" aria-hidden="true">
              ▾
            </span>
          </button>
        </li>
      </ul>

      {building && (
        <CustomMix
          tracks={tracks}
          filter={draft}
          onChange={setDraft}
          onStart={() => onChoose(CUSTOM_MIX_ID, draft)}
        />
      )}
    </section>
  );
}
